import type {
  BusinessSettings,
  PortfolioItem,
  Review,
  ReviewAggregates,
  SalonService,
} from '../types'

const hours = {
  open: '09:00',
  close: '20:00',
  closed: false,
}

export const defaultBusinessSettings: BusinessSettings = {
  businessName: 'Braids by Deb',
  phone: '+12145550192',
  email: 'bookings@braidsbydeb.com',
  address: {
    street: '2847 Oak Lawn Ave Suite 104',
    city: 'Dallas',
    state: 'TX',
    zip: '75219',
  },
  hours: {
    monday: hours,
    tuesday: hours,
    wednesday: hours,
    thursday: hours,
    friday: hours,
    saturday: hours,
    sunday: hours,
  },
  socialLinks: {
    instagram: 'https://www.instagram.com/braided_bydebs/',
    facebook: null,
    tiktok: 'https://www.tiktok.com/@braids_by_debs',
  },
  googleMapsUrl: 'https://www.google.com/maps/search/?api=1&query=Braids+by+Deb+Dallas',
  googleReviewUrl: 'https://www.google.com/maps/search/?api=1&query=Braids+by+Deb+Dallas',
  announcementBanner: null,
  bookingNotice: 'We confirm all appointments within 24 hours.',
}

// ── Braids by Deb service menu ─────────────────────────────────────────
// Mirrors lambdas/scripts/seed_data.py (ids, names, prices, durations) so
// mock mode matches what the real API serves after seeding. Prices in cents.
export const mockServices: SalonService[] = [
  // ── Box Braids ──────────────────────────────────────
  {
    serviceId: 'bb-small',
    name: 'Small Box Braids',
    category: 'braids-protective-styles',
    subcategory: 'box-braids',
    description: 'Small box braids with clean square parts, a lightweight feel, and beautiful length.',
    startingPrice: 20000,
    priceUnit: 'cents',
    durationMinutes: 360,
    imageUrl: '/images/small-box-braids.png',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 120,
  },
  {
    serviceId: 'bb-medium',
    name: 'Medium Box Braids',
    category: 'braids-protective-styles',
    subcategory: 'box-braids',
    description: 'Classic medium box braids — the everyday protective style, neat and versatile.',
    startingPrice: 18000,
    priceUnit: 'cents',
    durationMinutes: 300,
    imageUrl: '/images/medium-box-braids.jpg',
    imagePosition: 'top center',
    featured: true,
    active: true,
    bookingCount: 163,
  },
  {
    serviceId: 'bb-large',
    name: 'Large Box Braids',
    category: 'braids-protective-styles',
    subcategory: 'box-braids',
    description: 'Large box braids for a bold, quick-install look with plenty of movement.',
    startingPrice: 16000,
    priceUnit: 'cents',
    durationMinutes: 180,
    imageUrl: '/images/large-box-braids.png',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 88,
  },

  // ── Knotless Braids ─────────────────────────────────
  {
    serviceId: 'kl-small',
    name: 'Small Knotless Braids',
    category: 'braids-protective-styles',
    subcategory: 'knotless-braids',
    description: 'Small knotless braids with a natural lay and reduced scalp tension for lasting comfort.',
    startingPrice: 20000,
    priceUnit: 'cents',
    durationMinutes: 420,
    imageUrl: '/images/small-knotless-braids.jpg',
    imagePosition: 'top center',
    featured: true,
    active: true,
    bookingCount: 201,
  },
  {
    serviceId: 'kl-medium',
    name: 'Medium Knotless Braids',
    category: 'braids-protective-styles',
    subcategory: 'knotless-braids',
    description: 'Lightweight medium knotless braids that lay flat and feel weightless from day one.',
    startingPrice: 18000,
    priceUnit: 'cents',
    durationMinutes: 360,
    imageUrl: '/images/medium-knotless-braids.jpg',
    imagePosition: 'top center',
    featured: true,
    active: true,
    bookingCount: 187,
  },
  {
    serviceId: 'kl-large',
    name: 'Large Knotless Braids',
    category: 'braids-protective-styles',
    subcategory: 'knotless-braids',
    description: 'Large knotless braids with a soft, natural finish and a faster install time.',
    startingPrice: 16000,
    priceUnit: 'cents',
    durationMinutes: 240,
    imageUrl: '/images/large-knotless-braids.jpg',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 96,
  },

  // ── Boho Braids ─────────────────────────────────────
  {
    serviceId: 'boho-small',
    name: 'Small Boho Braids',
    category: 'braids-protective-styles',
    subcategory: 'boho-braids',
    description: 'Small boho braids with soft curly pieces for a romantic, dimensional finish.',
    startingPrice: 28000,
    priceUnit: 'cents',
    durationMinutes: 360,
    imageUrl: '/images/small-boho-braids.jpg',
    imagePosition: 'top center',
    featured: true,
    active: true,
    bookingCount: 109,
  },
  {
    serviceId: 'boho-medium',
    name: 'Medium Boho Braids',
    category: 'braids-protective-styles',
    subcategory: 'boho-braids',
    description: 'Medium boho braids blending sleek braids with loose curls for a boho-chic look.',
    startingPrice: 24000,
    priceUnit: 'cents',
    durationMinutes: 300,
    imageUrl: '/images/medium-boho-braids.jpg',
    imagePosition: 'top center',
    featured: true,
    active: true,
    bookingCount: 94,
  },
  {
    serviceId: 'boho-large',
    name: 'Large Boho Braids',
    category: 'braids-protective-styles',
    subcategory: 'boho-braids',
    description: 'Large boho braids with statement curls for effortless volume and texture.',
    startingPrice: 20000,
    priceUnit: 'cents',
    durationMinutes: 240,
    imageUrl: '/images/medium-twist-boho-braids.jpg',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 61,
  },

  // ── Twist Braids ────────────────────────────────────
  {
    serviceId: 'twist-small',
    name: 'Small Twist Braids',
    category: 'braids-protective-styles',
    subcategory: 'twist-braids',
    description: 'Small two-strand twists with a smooth finish, natural movement, and lasting hold.',
    startingPrice: 26000,
    priceUnit: 'cents',
    durationMinutes: 330,
    imageUrl: '/images/twist-braids.jpg',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 72,
  },
  {
    serviceId: 'twist-medium',
    name: 'Medium Twist Braids',
    category: 'braids-protective-styles',
    subcategory: 'twist-braids',
    description: 'Medium twists with a clean, defined finish — a lightweight protective favorite.',
    startingPrice: 22000,
    priceUnit: 'cents',
    durationMinutes: 270,
    imageUrl: '/images/twist-braids.jpg',
    imagePosition: 'top center',
    featured: true,
    active: true,
    bookingCount: 83,
  },
  {
    serviceId: 'twist-large',
    name: 'Large Twist Braids',
    category: 'braids-protective-styles',
    subcategory: 'twist-braids',
    description: 'Large twists for a bold, textured look with a quicker install.',
    startingPrice: 20000,
    priceUnit: 'cents',
    durationMinutes: 210,
    imageUrl: '/images/medium-twist-boho-braids.jpg',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 44,
  },

  // ── Cornrows ────────────────────────────────────────
  {
    serviceId: 'cornrows',
    name: 'Cornrows',
    category: 'braids-protective-styles',
    subcategory: 'cornrows',
    description: 'Neat straight-back or custom-pattern cornrows with clean parts and a comfortable install.',
    startingPrice: 18000,
    priceUnit: 'cents',
    durationMinutes: 180,
    imageUrl: '/images/straight-back-cornrows.jpg',
    imagePosition: 'top center',
    featured: true,
    active: true,
    bookingCount: 77,
  },

  // ── Fulani Braids ───────────────────────────────────
  {
    serviceId: 'fulani-small',
    name: 'Small Fulani Braids',
    category: 'braids-protective-styles',
    subcategory: 'fulani-braids',
    description: 'Small Fulani braids with delicate cornrow patterns and a refined protective finish.',
    startingPrice: 20000,
    priceUnit: 'cents',
    durationMinutes: 180,
    imageUrl: '/images/fulani-braids.jpg',
    imagePosition: 'top center',
    featured: true,
    active: true,
    bookingCount: 98,
  },
  {
    serviceId: 'fulani-medium',
    name: 'Medium Fulani Braids',
    category: 'braids-protective-styles',
    subcategory: 'fulani-braids',
    description: 'Traditional Fulani braids with signature center patterns and beautiful detailing.',
    startingPrice: 18000,
    priceUnit: 'cents',
    durationMinutes: 150,
    imageUrl: '/images/fulani-braids.jpg',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 65,
  },
  {
    serviceId: 'fulani-hairstyle',
    name: 'Fulani Hairstyle',
    category: 'braids-protective-styles',
    subcategory: 'fulani-braids',
    description: 'A styled Fulani look with braided patterns designed for a polished, finished result.',
    startingPrice: 15000,
    priceUnit: 'cents',
    durationMinutes: 180,
    imageUrl: '/images/fulani-hairstyle.jpg',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 39,
  },

  // ── Kids Braids ─────────────────────────────────────
  {
    serviceId: 'kids-cornrows',
    name: 'Kids Cornrows',
    category: 'kids',
    description: 'Gentle, neat cornrows for children — comfortable, quick, and confidence-boosting.',
    startingPrice: 12000,
    priceUnit: 'cents',
    durationMinutes: 90,
    imageUrl: '/images/kids-cornrows.jpg',
    imagePosition: 'top center',
    featured: true,
    active: true,
    bookingCount: 134,
  },
  {
    serviceId: 'kids-box',
    name: 'Kids Box Braids',
    category: 'kids',
    description: 'Kid-friendly box braids styled for comfort, neatness, and easy upkeep.',
    startingPrice: 16000,
    priceUnit: 'cents',
    durationMinutes: 150,
    imageUrl: '/images/kids-box-braids.png',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 71,
  },
  {
    serviceId: 'kids-knotless',
    name: 'Kids Knotless',
    category: 'kids',
    description: 'Lightweight knotless braids for kids with a soft, tension-free install.',
    startingPrice: 16000,
    priceUnit: 'cents',
    durationMinutes: 150,
    imageUrl: '/images/kids-braided-ponytails.jpg',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 58,
  },
  {
    serviceId: 'kids-other',
    name: 'Kids Other Styles',
    category: 'kids',
    description: 'Custom kids styles — twists, puffs, and beaded looks tailored to your little one.',
    startingPrice: 12000,
    priceUnit: 'cents',
    durationMinutes: 120,
    imageUrl: '/images/kids-braided-ponytails.jpg',
    imagePosition: 'top center',
    featured: false,
    active: true,
    bookingCount: 47,
  },
]

// ── Gallery / portfolio ────────────────────────────────────────────────
// The same nine looks the original site's "Fresh Out the Chair" gallery shows.
export const mockPortfolio: PortfolioItem[] = [
  {
    styleId: 'style-small-knotless',
    title: 'Small Knotless Braids',
    category: 'knotless',
    imageUrl: '/images/small-knotless-braids.jpg',
    thumbnailUrl: '/images/small-knotless-braids.jpg',
    featured: true,
    active: true,
    createdAt: '2026-04-18T12:00:00Z',
  },
  {
    styleId: 'style-medium-box',
    title: 'Medium Box Braids',
    category: 'box-braids',
    imageUrl: '/images/medium-box-braids.jpg',
    thumbnailUrl: '/images/medium-box-braids.jpg',
    featured: true,
    active: true,
    createdAt: '2026-04-12T12:00:00Z',
  },
  {
    styleId: 'style-small-boho',
    title: 'Small Boho Braids',
    category: 'boho',
    imageUrl: '/images/small-boho-braids.jpg',
    thumbnailUrl: '/images/small-boho-braids.jpg',
    featured: true,
    active: true,
    createdAt: '2026-04-05T12:00:00Z',
  },
  {
    styleId: 'style-fulani-braids',
    title: 'Fulani Braids',
    category: 'fulani',
    imageUrl: '/images/fulani-braids.jpg',
    thumbnailUrl: '/images/fulani-braids.jpg',
    featured: true,
    active: true,
    createdAt: '2026-03-29T12:00:00Z',
  },
  {
    styleId: 'style-twist-boho',
    title: 'Twist Boho Braids',
    category: 'boho',
    imageUrl: '/images/medium-twist-boho-braids.jpg',
    thumbnailUrl: '/images/medium-twist-boho-braids.jpg',
    featured: true,
    active: true,
    createdAt: '2026-03-22T12:00:00Z',
  },
  {
    styleId: 'style-goddess-cornrows',
    title: 'Goddess Cornrows',
    category: 'cornrows',
    imageUrl: '/images/goddess-cornrows.jpg',
    thumbnailUrl: '/images/goddess-cornrows.jpg',
    featured: false,
    active: true,
    createdAt: '2026-03-15T12:00:00Z',
  },
  {
    styleId: 'style-passion-twists',
    title: 'Passion Twists',
    category: 'twists',
    imageUrl: '/images/twist-braids.jpg',
    thumbnailUrl: '/images/twist-braids.jpg',
    featured: false,
    active: true,
    createdAt: '2026-03-08T12:00:00Z',
  },
  {
    styleId: 'style-fulani-hairstyle',
    title: 'Fulani Hairstyle',
    category: 'fulani',
    imageUrl: '/images/fulani-hairstyle.jpg',
    thumbnailUrl: '/images/fulani-hairstyle.jpg',
    featured: false,
    active: true,
    createdAt: '2026-03-01T12:00:00Z',
  },
  {
    styleId: 'style-straight-back-cornrows',
    title: 'Straight Back Cornrows',
    category: 'cornrows',
    imageUrl: '/images/straight-back-cornrows.jpg',
    thumbnailUrl: '/images/straight-back-cornrows.jpg',
    featured: false,
    active: true,
    createdAt: '2026-02-22T12:00:00Z',
  },
]

export const mockReviews: Review[] = [
  {
    reviewId: 'review-1',
    clientName: 'Kezia M.',
    rating: 5,
    body: "Deb is genuinely the best braider I've ever been to. My knotless braids lasted nearly 3 months and looked perfect the whole time. She took her time and made me feel so comfortable.",
    serviceName: 'Medium Knotless Braids',
    createdAt: '2026-04-22T12:00:00Z',
    source: 'website',
    status: 'approved',
    featured: true,
  },
  {
    reviewId: 'review-2',
    clientName: 'Jasmine T.',
    rating: 5,
    body: 'I got the Fulani hairstyle and it came out absolutely GORGEOUS. The partings, the beaded accents, the detail — Deb put so much love into every section. I got compliments for weeks straight.',
    serviceName: 'Fulani Hairstyle',
    createdAt: '2026-04-10T12:00:00Z',
    source: 'website',
    status: 'approved',
    featured: true,
  },
  {
    reviewId: 'review-3',
    clientName: 'Destiny W.',
    rating: 5,
    body: "Booked online — super easy. Left with the most beautiful cornrow design I've ever had, and my daughter loved her kids braids too. This is our new go-to spot!",
    serviceName: 'Cornrow Design',
    createdAt: '2026-03-28T12:00:00Z',
    source: 'website',
    status: 'approved',
    featured: true,
  },
  {
    reviewId: 'review-4',
    clientName: 'Nadia B.',
    rating: 5,
    body: 'Best boho braids I have ever had, and I have been natural for 10 years. The texture was perfect, the length was flawless, and she really listened to exactly what I wanted. 10 out of 10, no question.',
    serviceName: 'Small Boho Braids',
    createdAt: '2026-03-15T12:00:00Z',
    source: 'website',
    status: 'approved',
    featured: false,
  },
  {
    reviewId: 'review-5',
    clientName: 'Zora A.',
    rating: 5,
    body: 'My hair has never felt so healthy. Deb spent real time understanding my hair before touching it. Zero tension, clean parts, and my edges were treated with care. Highly recommend.',
    serviceName: 'Medium Box Braids',
    createdAt: '2026-02-20T12:00:00Z',
    source: 'website',
    status: 'approved',
    featured: false,
  },
  {
    reviewId: 'review-6',
    clientName: 'Fatou D.',
    rating: 5,
    body: 'Came in for Fulani braids and left feeling like royalty. The parts are so clean, the install was painless, and the style held up perfectly through two washes. Braids by Deb is a true gem in Dallas.',
    serviceName: 'Fulani Braids',
    createdAt: '2026-02-05T12:00:00Z',
    source: 'website',
    status: 'approved',
    featured: false,
  },
]

export const mockReviewAggregates: ReviewAggregates = {
  averageRating: 5.0,
  totalCount: 6,
}
